import React, { useEffect, useState } from 'react';
import { Container, Row, Col, Alert, Form, Button } from 'react-bootstrap';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { FaSearch } from 'react-icons/fa';
import ProductCard from '../components/ProductCard';
import { ProductGridSkeleton } from '../components/SkeletonLoader';
import { productsAPI } from '../services/api';
import SEO from '../components/SEO';

const Search = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const query = searchParams.get('q') || '';
  
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState(query);

  useEffect(() => {
    setSearchTerm(query);
    
    if (query.trim()) {
      fetchSearchResults(query);
    } else {
      setProducts([]);
    }
  }, [query]);

  const fetchSearchResults = async (searchQuery) => {
    setIsLoading(true);
    try {
      const response = await productsAPI.getAll({ search: searchQuery, limit: 50 });
      setProducts(response.data.products || response.data || []);
    } catch (error) {
      console.error('Search error:', error);
      setProducts([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchTerm.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  return (
    <>
      <SEO 
        title={query ? `Search: ${query}` : 'Search Products'}
        description={`Search results for "${query}" at IT World - Find software licenses and products.`}
      />
      <Container className="py-5">
        <h1 className="mb-4">
          <FaSearch className="me-2" />
          Search Products
        </h1>

        {/* Search Form */}
        <Form onSubmit={handleSearch} className="mb-4">
          <Row>
            <Col md={8} lg={6}>
              <div className="d-flex gap-2">
                <Form.Control
                  type="text"
                  placeholder="Search for products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="lg"
                />
                <Button type="submit" variant="primary" size="lg">
                  <FaSearch />
                </Button>
              </div>
            </Col>
          </Row>
        </Form>

        {/* Results */}
        {query && (
          <p className="text-muted mb-4">
            {isLoading ? 'Searching...' : `Found ${products.length} result(s) for "${query}"`}
          </p>
        )}

        {isLoading ? (
          <ProductGridSkeleton count={8} />
        ) : products.length > 0 ? (
          <Row>
            {products.map((product) => (
              <Col key={product._id} lg={3} md={4} sm={6} className="mb-4">
                <ProductCard product={product} />
              </Col>
            ))}
          </Row>
        ) : query ? (
          <Alert variant="info" className="text-center py-5">
            <FaSearch size={48} className="text-muted mb-3 d-block mx-auto" />
            <h4>No products found</h4>
            <p className="text-muted">
              Try different keywords or browse our categories.
            </p>
            <Button as="a" href="/products" variant="primary">
              Browse All Products
            </Button>
          </Alert>
        ) : (
          <Alert variant="secondary" className="text-center py-5">
            <FaSearch size={48} className="text-muted mb-3 d-block mx-auto" />
            <h4>Start searching</h4>
            <p className="text-muted">
              Enter a search term above to find products.
            </p>
          </Alert>
        )}
      </Container>
    </>
  );
};

export default Search;
